import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from "@/api/base44Client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Circle, Clock, AlertTriangle, ArrowRight, BookOpen } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from "@/lib/utils";
import OfficerUserManualDialog from './OfficerUserManualDialog';

export default function OfficerTaskInspector({ officer, open, onClose }) {
  const [showUserManual, setShowUserManual] = useState(false);
  
  // Fetch current user's profile for comparison
  const { data: currentUserProfile } = useQuery({
    queryKey: ['currentUserProfile'],
    queryFn: async () => {
      const user = await base44.auth.me();
      const profiles = await base44.entities.OfficerProfile.list();
      return profiles.find(p => p.user_email === user?.email);
    },
    enabled: open
  });

  // Fetch all active tasks for this officer to show current load
  const { data: tasks = [] } = useQuery({
    queryKey: ['officerTasks', officer?.user_email],
    queryFn: async () => {
      if (!officer?.user_email) return [];
      // Fetching all tasks and filtering might be heavy, but we want a global view of the officer's load
      // Ideally backend supports filtering. Assuming list returns all or we use a filter if available.
      // Using filter in memory for now as per previous patterns.
      const allTasks = await base44.entities.DailyTask.list();
      return allTasks.filter(t => t.user_email === officer.user_email && !t.completed);
    },
    enabled: !!officer?.user_email && open,
  });

  if (!officer) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="civ-officertaskinspector-dialog" className="bg-[#0B1221] border-slate-800 text-slate-100 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="relative">
                {officer.avatar_url ? (
                    <img src={officer.avatar_url} alt={officer.callsign} className="w-10 h-10 rounded-full object-cover border-2 border-slate-700" />
                ) : (
                    <div className="w-10 h-10 rounded-full bg-slate-800 border-2 border-slate-700 flex items-center justify-center text-slate-400 font-bold">
                        {officer.callsign?.substring(0, 2).toUpperCase()}
                    </div>
                )}
                {/* Status Dot */}
                <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full border-2 border-[#0B1221]" />
            </div>
            <div className="flex-1">
                <div className="text-lg font-bold">{officer.callsign}</div>
                <div className="text-xs text-slate-400 font-mono uppercase">{officer.rank_title} | {tasks.length} Active Tasks</div>
            </div>
            {/* User Manual Button */}
            <button
              onClick={() => setShowUserManual(true)}
              className="w-10 h-10 rounded-lg bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center hover:bg-emerald-500/30 transition-colors group"
              title="View User Manual"
            >
              <BookOpen className="w-5 h-5 text-emerald-400 group-hover:text-emerald-300" />
            </button>
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4">
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Current Active Load</h4>
            <ScrollArea className="h-[300px] pr-4">
                <div className="space-y-2">
                    {tasks.length === 0 ? (
                        <div className="text-sm text-slate-500 italic text-center py-4">No active tasks found.</div>
                    ) : (
                        tasks.map(task => (
                            <div key={task.id} className="bg-slate-900/50 p-3 rounded-lg border border-slate-800 flex gap-3">
                                <div className="mt-0.5">
                                    {task.status_flag === 'red' ? <AlertTriangle className="w-4 h-4 text-rose-500" /> :
                                     task.status_flag === 'green' ? <Clock className="w-4 h-4 text-emerald-500" /> :
                                     <Circle className="w-4 h-4 text-slate-600" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm text-slate-200 font-medium leading-tight">{task.task_description}</div>
                                    <div className="flex items-center gap-2 mt-1.5">
                                        <Badge variant="outline" className={cn("text-[10px] h-5 px-1.5 border-0", 
                                            task.status_flag === 'red' ? "bg-rose-500/10 text-rose-400" :
                                            task.status_flag === 'green' ? "bg-emerald-500/10 text-emerald-400" :
                                            "bg-slate-800 text-slate-400"
                                        )}>
                                            {task.status_flag === 'red' ? 'Struggling' : 
                                             task.status_flag === 'green' ? 'In Progress' : 'Pending'}
                                        </Badge>
                                        {task.date && (
                                            <span className="text-[10px] text-slate-500">{formatDistanceToNow(new Date(task.date), { addSuffix: true })}</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </ScrollArea>
        </div>
      </DialogContent>

      {/* User Manual Dialog */}
      <OfficerUserManualDialog
        officer={officer}
        open={showUserManual}
        onClose={() => setShowUserManual(false)}
        currentUserProfile={currentUserProfile}
      />
    </Dialog>
  );
}